import { createClient } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";

// Cliente aparte solo para subir adjuntos directo al bucket privado de
// OACS usando una URL firmada (la publishable key de OACS no es un
// secreto, ver .env). El resto de las operaciones pasan por la Edge
// Function "oacs-tickets" del propio proyecto de Dexterite.
const oacsStorage = createClient(
  import.meta.env.VITE_OACS_URL,
  import.meta.env.VITE_OACS_ANON_KEY,
);

const ATTACHMENTS_BUCKET = "oacs-interno";

export type TicketPriority = "baja" | "media" | "alta" | "critica";
export type TicketStatus =
  | "abierto"
  | "en_progreso"
  | "en_revision"
  | "resuelto"
  | "cerrado"
  | "reabierto"
  | "no_se_va_a_arreglar";

export interface Ticket {
  id: string;
  project_slug: string;
  reporter_email: string;
  reporter_name: string | null;
  title: string;
  content: unknown;
  priority: TicketPriority;
  status: TicketStatus;
  assigned_to_email: string | null;
  assigned_to_name: string | null;
  module: string | null;
  section: string | null;
  created_at: string;
  updated_at: string;
  first_response_at: string | null;
  resolved_at: string | null;
}

export interface TicketComment {
  id: string;
  ticket_id: string;
  author_email: string;
  author_name: string | null;
  content: unknown;
  is_status_change: boolean;
  old_status: TicketStatus | null;
  new_status: TicketStatus | null;
  created_at: string;
}

export interface TicketStats {
  tickets_mes_actual: number;
  tickets_resueltos_mes_actual: number;
  total_abiertos: number;
  total_en_progreso: number;
  total_en_revision: number;
  total_resueltos: number;
  total_cerrados: number;
  total_reabiertos: number;
  total_no_se_va_a_arreglar: number;
  por_prioridad: Record<TicketPriority, number>;
  tiempo_respuesta_promedio_horas: number | null;
  tiempo_resolucion_promedio_horas: number | null;
}

async function invoke<T>(action: string, params: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await supabase.functions.invoke("oacs-tickets", {
    body: { action, ...params },
  });

  if (error) {
    let message = error.message;
    const response: Response | undefined = (error as { context?: Response }).context;
    if (response && typeof response.json === "function") {
      try {
        const body = await response.json();
        if (body?.error) message = body.error;
      } catch {
        // el cuerpo no era JSON, nos quedamos con el mensaje genérico
      }
    }
    throw new Error(message);
  }

  if (data?.error) throw new Error(data.error);
  return data as T;
}

export async function crearTicket(params: {
  title: string;
  content: unknown;
  priority?: TicketPriority;
  module?: string;
  section?: string;
}): Promise<string> {
  const { id } = await invoke<{ id: string }>("create_ticket", params);
  return id;
}

export async function listarMisTickets(): Promise<Ticket[]> {
  const { tickets } = await invoke<{ tickets: Ticket[] }>("list_tickets", { all: false });
  return tickets;
}

export async function listarTodosTickets(): Promise<Ticket[]> {
  const { tickets } = await invoke<{ tickets: Ticket[] }>("list_tickets", { all: true });
  return tickets;
}

export async function obtenerTicket(
  ticketId: string,
): Promise<{ ticket: Ticket; comments: TicketComment[] }> {
  return invoke("get_ticket", { ticket_id: ticketId });
}

export async function agregarComentario(ticketId: string, content: unknown): Promise<string> {
  const { id } = await invoke<{ id: string }>("add_comment", { ticket_id: ticketId, content });
  return id;
}

export async function obtenerEstadisticas(): Promise<TicketStats> {
  return invoke<TicketStats>("get_stats");
}

export async function subirAdjunto(file: File): Promise<{ path: string; contentType: string }> {
  const { path, token } = await invoke<{ path: string; token: string }>("sign_upload", {
    filename: file.name,
  });
  const { error } = await oacsStorage.storage
    .from(ATTACHMENTS_BUCKET)
    .uploadToSignedUrl(path, token, file);
  if (error) throw new Error(error.message);
  return { path, contentType: file.type };
}

export async function urlLecturaAdjunto(path: string): Promise<string> {
  const { url } = await invoke<{ url: string }>("sign_read", { path });
  return url;
}

export async function confirmarResolucion(
  ticketId: string,
  accion: "cerrar" | "reabrir",
  content?: unknown,
): Promise<void> {
  await invoke("confirmar_resolucion", { ticket_id: ticketId, accion, content: content ?? null });
}
